<script setup lang="ts">
import {moneyFormat} from "~/utils/util";
import {formatUsDateToBr} from "~/utils/datetime";
import type {Event} from "~/types/event";
import type {Ticket} from "~/types/ticket";

const props = defineProps<{
  event: Event
}>()

const {eventCheckout} = useCheckout()
const {navigateToOrSignIn} = useRedirectLogin()
const selectedTickets = reactive(new Map<string, number>())
const selectedTicketsLength = computed(() => selectedTickets.size)

const totalSelected = (ticket: Ticket) => {
  return selectedTickets.get(<string>ticket.id) || 0
}

const plusTicket = (ticket: Ticket) => {
  const qtd = totalSelected(ticket)
  selectedTickets.set(<string>ticket.id, qtd + 1)
}

const minusTicket = (ticket: Ticket) => {
  const qtd = totalSelected(ticket)
  selectedTickets.set(<string>ticket.id, qtd - 1)
}

const periodValid = (ticket: Ticket): string | null => {
  const date = new Date();
  const start = new Date(ticket.startAt)
  const end = new Date(ticket.endAt)
  if (date > end) return "Encerrado"
  if (start > date) return "Inicia em " + formatUsDateToBr(start)
  return null
}

const totalTickets = computed(() => {
  let total = 0
  for (const [id, qtd] of selectedTickets.entries()) {
    const t = props.event.tickets.find(t => t.id === id)
    total += qtd * ((t?.amount || 0) + (t?.fee || 0))
  }
  return total
})

const buyTickets = () => {
  eventCheckout.value = {
    id: props.event?.id || '',
    name: props.event.name,
    logo: props.event.logo,
    startAt: props.event.startAt,
    slug: props.event.slug,
    tickets: []
  }
  for (const [id, qtd] of selectedTickets.entries()) {
    const t: Ticket | undefined = props.event.tickets.find(t => t.id === id)
    if (t) {
      eventCheckout.value.tickets.push({
        id: t?.id || '',
        title: t.title,
        amount: t.amount,
        fee: t.fee,
        qtd: qtd,
        qtdMin: t.quantityMinCheckout,
        endAt: t.endAt,
        obs: t.obs
      })
    }
  }
  navigateToOrSignIn(`/eventos/${props.event.slug}/checkout`)
}
</script>

<template>
  <div class="col-md-4 pt-md-0 sell" style="margin-top: -6rem;">
    <div class="sticky-top pt-5">
      <div class="card text-dark bg-faded-accent rounded-3 p-2 mt-5">
        <div class="card-header"><h4 class="h4 mb-0">Ingressos</h4></div>
        <div class="card-body py-0">
          <div v-for="(ticket, index) in event.tickets" :key="index" class="mb-2">
            <div>
              <strong>{{ ticket.title }} </strong>
              <Icon icon="mingcute:question-fill" data-bs-toggle="tooltip" class="text-info icon-size ps-2"
                    :title="ticket.obs"/>
            </div>
            <div class="row">
              <ul class="list-unstyled mb-0 col-8">
                <li style="line-height: 1">
                  <p class="mt-0 mb-1">R$ {{ moneyFormat(ticket.amount) }} ( + R$ {{ moneyFormat(ticket.fee) }}
                    taxa)</p>
                  <small>Vendas até {{ formatUsDateToBr(ticket.endAt) }}</small>
                </li>
              </ul>
              <div class="col-4 d-flex align-items-center justify-content-center">
                <small v-if="periodValid(ticket)">{{ periodValid(ticket) }}</small>
                <template v-else>
                  <button type="button" class="btn btn-icon btn-info btn-xxs"
                          :disabled="totalSelected(ticket) < 1"
                          @click="minusTicket(ticket)"
                  >
                    <Icon icon="icons8:minus"/>
                  </button>
                  <span style="padding: 0 8px">{{ totalSelected(ticket) }}</span>
                  <button type="button" class="btn btn-icon btn-info btn-xxs"
                          :disabled="totalSelected(ticket) >= ticket.quantityMaxCheckout"
                          @click="plusTicket(ticket)"
                  >
                    <Icon icon="icons8:plus"/>
                  </button>
                </template>
              </div>
            </div>
            <hr class="mt-2 mb-2">
          </div>
          <div v-if="totalTickets > 0" class="d-inline-block mb-2">
            Total <b>{{ moneyFormat(totalTickets) }}</b>&nbsp;
            <small class="float-end"> em até 10x</small>
          </div>
          <button class="btn btn-lg btn-primary w-100 mb-3" :disabled="selectedTicketsLength <= 0" @click="buyTickets">
            {{ selectedTicketsLength > 0 ? 'Comprar Ingressos' : 'Selecione um ingresso' }}
          </button>
          <!--          <a class="d-inline-block mb-2 pb-2 text-decoration-none" href="javascript:void(0);">-->
          <!--            <i class="fi-help me-2 mt-n1 align-middle"></i>Entenda nossas taxas-->
          <!--          </a>-->
          <h5 class="mb-0 pb-2">Formas de Pagamento</h5>
          <ul class="list-unstyled mt-n2 mb-0 mb-3">
            <li class="mt-2 mb-0">
              <Icon icon="logos:mastercard" class="icon-size"/>&nbsp;
              <Icon icon="logos:visa" class="icon-size" style="font-size: 12px"/>&nbsp;
              <Icon icon="logos:elo" class="icon-size"/>&nbsp;
              <Icon icon="fontisto:dinners-club" class="icon-size" style="color: #213673"/>&nbsp;
              <Icon icon="simple-icons:americanexpress" class="icon-size" style="color: #213673"/>&nbsp;
              <Icon icon="ic:sharp-pix" class="icon-size" style="color: #21736d;font-size: 22px"/>&nbsp;
            </li>
            <!--            <li class="mt-2 mb-0 text-danger"><b>Até 4x no Cartão de Crédito</b></li>-->
          </ul>
        </div>
      </div>
      <EventAgeGroup :age-group="event.ageGroup" class="mt-3" />
    </div>
  </div>
</template>
<style>
.sell small {
  color: var(--fi-gray);
}

.sell hr {
  border-color: var(--fi-gray);
}
</style>
